import React, { useCallback, useState } from 'react';
import { Alert, Button, Card, Form, ListGroup, ProgressBar, Spinner } from 'react-bootstrap';
import { FiUpload, FiFile, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import { FileImportInfo, ImportResult, ImportType } from '../../types/imports';

export interface FileImportProps {
  tipo: ImportType;
  title: string;
  description?: string;
  acceptedFormats?: string[];
  maxFiles?: number;
  onImport: (file: File) => Promise<ImportResult>;
  templateUrl?: string;
}

export const FileImport: React.FC<FileImportProps> = ({
  tipo,
  title,
  description,
  acceptedFormats = ['.csv', '.xlsx', '.xls'],
  maxFiles = 5,
  onImport,
  templateUrl,
}) => {
  const [files, setFiles] = useState<FileImportInfo[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const generateId = () => Math.random().toString(36).substring(7);

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const filesArray = Array.from(newFiles);

      // Validar formato
      const validFiles = filesArray.filter((file) => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        return acceptedFormats.some((format) => format.toLowerCase() === ext);
      });

      if (validFiles.length !== filesArray.length) {
        alert(
          `Apenas arquivos ${acceptedFormats.join(', ')} são aceitos.`
        );
      }

      // Limitar número de arquivos
      const currentCount = files.filter((f) => f.status !== 'completed').length;
      const remaining = maxFiles - currentCount;
      const filesToAdd = validFiles.slice(0, remaining);

      if (filesToAdd.length < validFiles.length) {
        alert(`Limite de ${maxFiles} arquivos simultâneos atingido.`);
      }

      const newFileInfos: FileImportInfo[] = filesToAdd.map((file) => ({
        file,
        id: generateId(),
        status: 'pending',
        progress: 0,
      }));

      setFiles((prev) => [...prev, ...newFileInfos]);
    },
    [acceptedFormats, maxFiles, files]
  );

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(event.target.files);
      event.target.value = ''; // Reset input
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files) {
      addFiles(event.dataTransfer.files);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const processFile = async (fileInfo: FileImportInfo) => {
    const updateFile = (updates: Partial<FileImportInfo>) => {
      setFiles((prev) =>
        prev.map((f) => (f.id === fileInfo.id ? { ...f, ...updates } : f))
      );
    };

    try {
      updateFile({ status: 'processing', progress: 10 });

      const result = await onImport(fileInfo.file);

      updateFile({
        status: result.success ? 'completed' : 'failed',
        progress: 100,
        result,
        error: result.success ? undefined : result.message,
      });
    } catch (error: any) {
      console.error('Error processing file:', error);
      updateFile({
        status: 'failed',
        progress: 0,
        error: error?.message || 'Erro ao processar arquivo',
      });
    }
  };

  const handleUpload = async (fileInfo: FileImportInfo) => {
    await processFile(fileInfo);
  };

  const handleUploadAll = async () => {
    const pendingFiles = files.filter((f) => f.status === 'pending');
    for (const fileInfo of pendingFiles) {
      await processFile(fileInfo);
    }
  };

  const handleRemove = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleClearCompleted = () => {
    setFiles((prev) => prev.filter((f) => f.status !== 'completed'));
  };

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const processingCount = files.filter((f) => f.status === 'processing').length;
  const completedCount = files.filter((f) => f.status === 'completed').length;
  const failedCount = files.filter((f) => f.status === 'failed').length;

  return (
    <div>
      <Card>
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">
            <FiUpload className="me-2" />
            {title}
          </h5>
        </Card.Header>
        <Card.Body>
          {description && (
            <Alert variant="info" className="mb-3">
              <small>{description}</small>
            </Alert>
          )}

          {templateUrl && (
            <div className="mb-3">
              <a
                href={templateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-secondary"
              >
                <FiFile className="me-1" />
                Baixar Template
              </a>
            </div>
          )}

          {/* Área de Drop */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border rounded p-4 text-center mb-3 ${
              isDragging ? 'border-primary bg-light' : 'border-secondary'
            }`}
            style={{
              borderStyle: 'dashed',
              borderWidth: '2px',
              cursor: 'pointer',
              transition: 'all 0.3s',
            }}
          >
            <FiUpload size={48} className="text-muted mb-3" />
            <p className="mb-2">
              Arraste e solte arquivos aqui ou clique para selecionar
            </p>
            <Form.Control
              type="file"
              multiple
              accept={acceptedFormats.join(',')}
              onChange={handleFileInput}
              style={{ display: 'none' }}
              id={`file-input-${tipo}`}
            />
            <label htmlFor={`file-input-${tipo}`} className="btn btn-primary">
              Selecionar Arquivos
            </label>
            <div className="mt-2">
              <small className="text-muted">
                Formatos aceitos: {acceptedFormats.join(', ')} (máximo {maxFiles}{' '}
                arquivos)
              </small>
            </div>
          </div>

          {/* Ações em Lote */}
          {files.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleUploadAll}
                  disabled={pendingCount === 0 || processingCount > 0}
                  className="me-2"
                >
                  <FiUpload className="me-1" />
                  Processar Todos ({pendingCount})
                </Button>
                {completedCount > 0 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleClearCompleted}
                  >
                    Limpar Concluídos
                  </Button>
                )}
              </div>
              <div className="text-muted">
                <small>
                  {pendingCount > 0 && `${pendingCount} pendente${pendingCount > 1 ? 's' : ''} • `}
                  {processingCount > 0 && `${processingCount} processando • `}
                  {completedCount > 0 && `${completedCount} concluído${completedCount > 1 ? 's' : ''} • `}
                  {failedCount > 0 && `${failedCount} falha${failedCount > 1 ? 's' : ''}`}
                </small>
              </div>
            </div>
          )}

          {/* Lista de Arquivos */}
          {files.length > 0 ? (
            <ListGroup>
              {files.map((fileInfo) => (
                <ListGroup.Item key={fileInfo.id}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                        {fileInfo.status === 'completed' && (
                          <FiCheckCircle className="text-success me-2" />
                        )}
                        {fileInfo.status === 'failed' && (
                          <FiXCircle className="text-danger me-2" />
                        )}
                        {fileInfo.status === 'processing' && (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                        )}
                        <strong>{fileInfo.file.name}</strong>
                      </div>
                      <small className="text-muted">
                        {(fileInfo.file.size / 1024).toFixed(2)} KB
                      </small>
                    </div>
                    <div>
                      {fileInfo.status === 'pending' && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleUpload(fileInfo)}
                        >
                          Processar
                        </Button>
                      )}
                      {fileInfo.status !== 'processing' && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemove(fileInfo.id)}
                          className="ms-2"
                        >
                          Remover
                        </Button>
                      )}
                    </div>
                  </div>

                  {fileInfo.status === 'processing' && (
                    <ProgressBar
                      now={fileInfo.progress}
                      label={`${fileInfo.progress}%`}
                      animated
                      className="mb-2"
                    />
                  )}

                  {fileInfo.result && (
                    <Alert
                      variant={fileInfo.result.success ? 'success' : 'danger'}
                      className="mb-0 mt-2"
                    >
                      <div className="d-flex justify-content-between">
                        <div>
                          <strong>
                            {fileInfo.result.success ? 'Sucesso' : 'Falha'}
                          </strong>
                          <div>
                            <small>
                              Total: {fileInfo.result.total_rows} •{' '}
                              Sucesso: {fileInfo.result.success_count} •{' '}
                              Erros: {fileInfo.result.error_count}
                              {fileInfo.result.skipped_rows && fileInfo.result.skipped_rows.length > 0 && (
                                <> • Ignorados: {fileInfo.result.skipped_rows.length}</>
                              )}
                            </small>
                          </div>
                        </div>
                      </div>
                      {fileInfo.result.message && (
                        <div className="mt-2">
                          <small>{fileInfo.result.message}</small>
                        </div>
                      )}
                      
                      {/* Dependências Ausentes */}
                      {fileInfo.result.missing_dependencies && 
                       fileInfo.result.missing_dependencies.length > 0 && (
                        <Alert variant="warning" className="mt-2 mb-2">
                          <div className="d-flex align-items-center mb-2">
                            <FiAlertCircle className="me-2" />
                            <strong>Dependências Ausentes</strong>
                          </div>
                          <small className="d-block mb-2">
                            Os seguintes itens precisam ser criados antes da importação:
                          </small>
                          <details>
                            <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                              <small>
                                Ver dependências ({fileInfo.result.missing_dependencies.length})
                              </small>
                            </summary>
                            <ListGroup variant="flush" className="mt-2">
                              {fileInfo.result.missing_dependencies.map((dep, idx) => (
                                <ListGroup.Item key={idx} className="py-2 px-0 border-0">
                                  <div className="mb-1">
                                    <strong className="text-primary">
                                      {dep.entity_type}:
                                    </strong>{' '}
                                    <code className="bg-light px-1">{dep.identifier}</code>
                                  </div>
                                  <small className="text-muted d-block">
                                    Campo: <code>{dep.field}</code> • 
                                    Linhas afetadas: {dep.rows.join(', ')}
                                  </small>
                                  {dep.required_fields && (
                                    <details className="mt-1">
                                      <summary style={{ cursor: 'pointer' }}>
                                        <small className="text-info">Campos necessários</small>
                                      </summary>
                                      <div className="ms-3 mt-1">
                                        {Object.entries(dep.required_fields).map(([key, value]) => (
                                          <small key={key} className="d-block text-muted">
                                            <code>{key}</code>: {value}
                                          </small>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          </details>
                        </Alert>
                      )}
                      
                      {fileInfo.result.errors && fileInfo.result.errors.length > 0 && (
                        <details className="mt-2">
                          <summary style={{ cursor: 'pointer' }}>
                            <small>Ver erros ({fileInfo.result.errors.length})</small>
                          </summary>
                          <ListGroup className="mt-2" variant="flush">
                            {fileInfo.result.errors.slice(0, 10).map((err, idx) => (
                              <ListGroup.Item key={idx} className="py-1 px-0">
                                <small>
                                  <FiAlertCircle className="me-1" />
                                  Linha {err.row}
                                  {err.field && ` - ${err.field}`}: {err.message}
                                </small>
                              </ListGroup.Item>
                            ))}
                            {fileInfo.result.errors.length > 10 && (
                              <ListGroup.Item className="py-1 px-0">
                                <small className="text-muted">
                                  ... e mais {fileInfo.result.errors.length - 10} erros
                                </small>
                              </ListGroup.Item>
                            )}
                          </ListGroup>
                        </details>
                      )}
                    </Alert>
                  )}

                  {fileInfo.error && !fileInfo.result && (
                    <Alert variant="danger" className="mb-0 mt-2">
                      <small>
                        <FiXCircle className="me-1" />
                        {fileInfo.error}
                      </small>
                    </Alert>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <Alert variant="secondary" className="text-center mb-0">
              <small>Nenhum arquivo selecionado</small>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};
